import React, { useState, useCallback, useEffect, useRef } from 'react';
import SearchableSelect from './components/SearchableSelect';
import VisaResult from './components/VisaResult';
import PopularRoutes from './components/PopularRoutes';
import FaqSection from './components/FaqSection';
import PassportExplorer from './components/PassportExplorer';
import RecentRoutes from './components/RecentRoutes';
import { COUNTRIES } from './data/visa-data';
import { ApiResponseData } from './utils/types';
import { checkVisaRequirements } from './services/visa-service';
import { useRouteHistory } from './hooks/useRouteHistory';

const LOADING_STEPS = [
  { label: 'Fetching route data', sub: 'Connecting to live embassy databases...' },
  { label: 'Analyzing requirements', sub: 'Cross-referencing visa policies and border rules...' },
  { label: 'Finalizing results', sub: 'Applying regional exceptions and special conditions...' },
];

function getActionableError(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes('server configuration') || lower.includes('missing api key')) {
    return 'Service is temporarily unavailable. Please try again shortly.';
  }
  if (lower.includes('failed to synthesize') || lower.includes('synthesis failed')) {
    return "Our AI service couldn't process this route. Please try again.";
  }
  if (lower.includes('rate limit') || lower.includes('429')) {
    return 'Too many requests. Please wait a moment and try again.';
  }
  if (lower.includes('failed to fetch') || lower.includes('networkerror')) {
    return 'Connection failed. Please check your internet and try again.';
  }
  return message;
}

function App() {
  const [passportCode, setPassportCode] = useState('');
  const [destinationCode, setDestinationCode] = useState('');
  const [result, setResult] = useState<ApiResponseData | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const { history, addRoute, removeRoute, clearHistory } = useRouteHistory();

  useEffect(() => {
    if (!isLoading) { setLoadingStep(0); return; }
    const interval = setInterval(() => {
      setLoadingStep(prev => Math.min(prev + 1, LOADING_STEPS.length - 1));
    }, 1800);
    return () => clearInterval(interval);
  }, [isLoading]);

  useEffect(() => {
    if (result && window.innerWidth < 768) {
      resultRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [result]);

  const handleCheck = useCallback(async (pCode?: string, dCode?: string) => {
    const p = pCode || passportCode;
    const d = dCode || destinationCode;
    if (!p || !d) return;
    if (p === d) { setError('Passport and destination cannot be the same.'); return; }

    setError(null);
    setIsLoading(true);
    setResult(null);

    try {
      const response = await checkVisaRequirements(p, d);
      setResult(response.data);
      setShowResult(true);
      addRoute({
        passportCode: p,
        passportName: response.data.passport.name,
        destinationCode: d,
        destinationName: response.data.destination.name,
        ruleName: response.data.visa_rules.primary_rule.name,
        color: response.data.visa_rules.primary_rule.color,
      });
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('event', 'visa_lookup', {
          event_category: 'engagement',
          event_label: `${p} → ${d}`,
          passport_country: p,
          destination_country: d,
          visa_type: response.data.visa_rules?.primary_rule?.name || 'unknown',
        });
      }
    } catch (err: any) {
      const message = err.message || 'Unable to retrieve requirements. Please verify your selection.';
      setError(getActionableError(message));
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('event', 'visa_lookup_error', {
          event_category: 'error',
          event_label: `${p} → ${d}`,
          error_message: message,
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [passportCode, destinationCode]);

  const handlePopularSelect = (fromCode: string, toCode: string) => {
    setPassportCode(fromCode);
    setDestinationCode(toCode);
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'popular_route_click', {
        event_category: 'navigation',
        event_label: `${fromCode} → ${toCode}`,
      });
    }
    handleCheck(fromCode, toCode);
  };

  const getCountryNameByCode = (code: string) =>
    COUNTRIES.find(c => c.code === code)?.name || '';

  const [view, setView] = useState<'checker' | 'explorer'>('checker');

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">

      {/* ── Navbar ── */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-slate-100 no-print">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-blue-600 rounded-md flex items-center justify-center shadow-sm">
              <span className="text-white font-black text-xs italic">V</span>
            </div>
            <span className="font-bold text-slate-900 text-[15px]">Visa Hub</span>
          </div>
          <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
            <button
              onClick={() => setView('checker')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${view === 'checker' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Visa Checker
            </button>
            <button
              onClick={() => setView('explorer')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${view === 'explorer' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Passport Explorer
            </button>
          </div>
        </div>
      </nav>

      {/* ── Explorer view ── */}
      {view === 'explorer' && (
        <main className="flex-1">
          <PassportExplorer />
        </main>
      )}

      {/* ── Checker view ── */}
      {view === 'checker' && <>

      {/* ── Hero ── */}
      <div className="bg-white border-b border-slate-100 no-print">
        <div className="max-w-5xl mx-auto px-6 py-16 md:py-20 text-center">
          <h1 className="text-4xl sm:text-5xl md:text-[3.5rem] font-black text-slate-900 tracking-tight leading-[1.1] mb-4">
            Know before you go.
          </h1>
          <p className="text-slate-500 text-lg leading-relaxed max-w-md mx-auto">
            Instant visa requirements and entry policies for any passport and destination worldwide.
          </p>
        </div>
      </div>

      {/* ── Main ── */}
      <main className="flex-grow">
        <div className="max-w-5xl mx-auto px-6 py-10">

          {/* Search card */}
          <div className={`transition-all duration-500 ${showResult || isLoading ? 'mb-8' : 'max-w-2xl mx-auto mb-8'}`}>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8 no-print">
              <div className="flex flex-col md:flex-row gap-4 md:items-end">
                <div className="flex-1">
                  <SearchableSelect
                    label="Passport Country"
                    options={COUNTRIES}
                    value={getCountryNameByCode(passportCode)}
                    onChange={(name) => {
                      setPassportCode(COUNTRIES.find(c => c.name === name)?.code || '');
                      setError(null);
                    }}
                    placeholder="Select passport"
                  />
                </div>

                <div className="hidden md:flex flex-shrink-0 pb-1 text-slate-300">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>

                <div className="flex-1">
                  <SearchableSelect
                    label="Destination"
                    options={COUNTRIES}
                    value={getCountryNameByCode(destinationCode)}
                    onChange={(name) => {
                      setDestinationCode(COUNTRIES.find(c => c.name === name)?.code || '');
                      setError(null);
                    }}
                    placeholder="Select destination"
                  />
                </div>

                <div className="flex-shrink-0">
                  <button
                    onClick={() => handleCheck()}
                    disabled={!passportCode || !destinationCode || isLoading}
                    className="w-full md:w-auto h-11 px-6 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed bg-blue-600 hover:bg-blue-700 active:scale-95 text-white shadow-sm"
                  >
                    {isLoading ? (
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : (
                      <>
                        Check
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div className="mt-4 flex items-start gap-2.5 p-3.5 bg-rose-50 border border-rose-100 rounded-xl text-rose-700 text-sm font-medium">
                  <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {error}
                </div>
              )}
            </div>

            <RecentRoutes
              history={history}
              onSelect={(p, d) => { setPassportCode(p); setDestinationCode(d); handleCheck(p, d); }}
              onRemove={removeRoute}
              onClear={clearHistory}
            />

            <PopularRoutes onSelect={handlePopularSelect} />
          </div>

          {/* Loading state */}
          {isLoading && (
            <div ref={resultRef} id="result-section" className="bg-white rounded-2xl border border-slate-200 shadow-sm px-8 py-14 flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-5">
                <svg className="w-6 h-6 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
              <p className="font-bold text-slate-900 text-lg mb-1 transition-all duration-500">
                {LOADING_STEPS[loadingStep].label}
              </p>
              <p className="text-slate-400 text-sm mb-7 transition-all duration-500">
                {LOADING_STEPS[loadingStep].sub}
              </p>
              <div className="flex items-center gap-2">
                {LOADING_STEPS.map((_, i) => (
                  <div key={i} className={`rounded-full transition-all duration-500 ${
                    i < loadingStep ? 'w-2 h-2 bg-blue-600' :
                    i === loadingStep ? 'w-2.5 h-2.5 bg-blue-600' :
                    'w-2 h-2 bg-slate-200'
                  }`} />
                ))}
              </div>
            </div>
          )}

          {/* Result */}
          {!isLoading && showResult && result && (
            <div ref={resultRef} id="result-section">
              <VisaResult data={result} />
            </div>
          )}
        </div>

        <FaqSection />
      </main>

      {/* ── Footer ── */}
      <footer className="bg-white border-t border-slate-100 py-8 px-6 no-print" role="contentinfo">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-blue-600 rounded-md flex items-center justify-center shadow-sm">
              <span className="text-white font-black text-xs italic">V</span>
            </div>
            <span className="font-bold text-slate-900 text-[15px]">Visa Hub</span>
          </div>
          <div className="flex items-center gap-4">
            <p className="text-slate-400 text-xs">
              Always verify requirements with official consulates before travel
            </p>
            <a href="/privacy" className="text-slate-400 text-xs hover:text-slate-600 transition-colors whitespace-nowrap">
              Privacy Policy
            </a>
            <a href="/faq" className="text-slate-400 text-xs hover:text-slate-600 transition-colors whitespace-nowrap">
              FAQ
            </a>
          </div>
        </div>
      </footer>
      </>}
    </div>
  );
}

export default App;
